import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { whatsappPreorderReady } from "@/lib/whatsapp";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MagnifyingGlass, CheckCircle, ArrowCounterClockwise, Trash, WhatsappLogo, Plus, X, PencilSimple } from "@phosphor-icons/react";

const inputCls = "w-full px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150";
const labelCls = "block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5";

const fmtDate = (d) => { const p = new Date(d); const z = (n) => String(n).padStart(2, "0"); return `${z(p.getDate())}-${z(p.getMonth() + 1)}-${String(p.getFullYear()).slice(2)}`; };

export default function Prenotazioni() {
  const { user } = useAuth();
  const [preorders, setPreorders] = useState([]);
  const [filter, setFilter] = useState("attesa");
  const [sortBy, setSortBy] = useState("recenti");
  const [tags, setTags] = useState([]);
  const [tagFilter, setTagFilter] = useState(null);
  const [formTag, setFormTag] = useState("");
  const [addingTag, setAddingTag] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [item, setItem] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editItem, setEditItem] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editTag, setEditTag] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    const params = {};
    if (filter) params.status = filter;
    if (q) params.q = q;
    if (tagFilter) params.tag = tagFilter;
    params.sort = sortBy;
    const { data } = await api.get("/preorders", { params });
    setPreorders(data);
  };

  useEffect(() => { load().catch(() => {}); }, [filter, q, sortBy, tagFilter]);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const positions = Object.fromEntries(
    [...preorders].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))).map((x, i) => [x.id, i + 1])
  );

  const loadTags = async () => {
    try { const { data } = await api.get("/preorder-tags"); setTags(data); } catch { /* ignore */ }
  };

  useEffect(() => { loadTags(); }, []);

  const addTag = async () => {
    if (!newTag.trim()) return;
    try {
      const { data } = await api.post("/preorder-tags", { name: newTag.trim() });
      setTags(data);
      setNewTag("");
      setAddingTag(false);
      toast.success("Categoria aggiunta");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeTag = async (t) => {
    try {
      const { data } = await api.delete(`/preorder-tags/${encodeURIComponent(t)}`);
      setTags(data);
      if (tagFilter === t) setTagFilter(null);
      if (formTag === t) setFormTag("");
      toast.success("Categoria rimossa");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => {
    if (selectedClient || clientQuery.trim().length < 2) { setClientResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { q: clientQuery.trim(), limit: 8 } });
        setClientResults(data);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [clientQuery, selectedClient]);

  const pickClient = (c) => {
    setSelectedClient(c);
    setClientName(c.name);
    setClientPhone(c.phone || "");
    setClientQuery("");
    setClientResults([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!clientName.trim()) { toast.error("Inserisci il nome del cliente"); return; }
    if (!item.trim()) { toast.error("Descrivi cosa vuole prenotare il cliente"); return; }
    if (tags.length > 0 && !formTag) { toast.error("Seleziona una categoria per la richiesta"); return; }
    setSaving(true);
    try {
      let cid = selectedClient?.id;
      if (!cid) {
        const { data: found } = await api.get("/clients", { params: { q: clientName.trim(), limit: 10 } });
        const exact = found.find((c) => c.name.trim().toLowerCase() === clientName.trim().toLowerCase());
        if (exact) cid = exact.id;
        else {
          const { data: nc } = await api.post("/clients", { name: clientName.trim(), phone: clientPhone.trim() });
          cid = nc.id;
        }
      }
      await api.post("/preorders", { client_id: cid, item: item.trim(), notes: notes.trim(), tag: formTag || undefined });
      toast.success("Richiesta aggiunta");
      setSelectedClient(null); setClientName(""); setClientPhone(""); setItem(""); setNotes(""); setFormTag("");
      setFilter("attesa");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/preorders/${id}`, { status });
      toast.success(status === "consegnato" ? "Segnato come consegnato" : "Riportato in attesa");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditItem(p.item || "");
    setEditNotes(p.notes || "");
    setEditTag(p.tag || "");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editItem.trim()) { toast.error("Descrivi cosa vuole prenotare il cliente"); return; }
    if (tags.length > 0 && !editTag) { toast.error("Seleziona una categoria per la richiesta"); return; }
    setEditSaving(true);
    try {
      await api.patch(`/preorders/${editing.id}`, { item: editItem.trim(), notes: editNotes.trim(), tag: editTag });
      toast.success("Richiesta aggiornata — posizione in coda mantenuta");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/preorders/${id}`);
      toast.success("Richiesta eliminata");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 space-y-8" data-testid="prenotazioni-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Lista d'attesa nuovi arrivi</p>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none">Richieste</h1>
      </div>

      <form onSubmit={submit} className="border border-border rounded-md bg-card p-6" data-testid="preorder-form">
        <h2 className="font-heading font-bold tracking-tight mb-5">Nuova richiesta</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-5">
            <div className="relative">
              <label className={labelCls}>Cliente (cerca per nome o telefono)</label>
              <input value={clientQuery}
                onChange={(e) => { setClientQuery(e.target.value); setSelectedClient(null); }}
                data-testid="preorder-client-search-input" className={inputCls} placeholder="Es. Rossi oppure 333 1234567" />
              {clientResults.length > 0 && (
                <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-popover border border-border rounded-md" data-testid="preorder-client-results">
                  {clientResults.map((c) => (
                    <button key={c.id} type="button" onClick={() => pickClient(c)} data-testid={`preorder-client-result-${c.id}`}
                      className="w-full text-left px-4 py-2.5 hover:bg-accent transition-colors duration-150 flex justify-between items-center">
                      <span className="text-sm font-semibold">{c.name}</span>
                      <span className="text-xs text-muted-foreground">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nome cliente *</label>
                <input required value={clientName} onChange={(e) => setClientName(e.target.value.replace(/\d/g, ""))}
                  data-testid="preorder-client-name-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Telefono</label>
                <input required inputMode="tel" value={clientPhone} onChange={(e) => setClientPhone(e.target.value.replace(/[^\d+]/g, ""))}
                  data-testid="preorder-client-phone-input" className={inputCls} />
              </div>
            </div>
            {selectedClient && (
              <p className="text-xs text-green-500 font-semibold flex items-center gap-1.5" data-testid="preorder-client-selected">
                <CheckCircle size={13} weight="fill" /> Cliente esistente selezionato
              </p>
            )}
          </div>

          <div className="space-y-5">
            <div>
              <label className={labelCls}>Cosa vuole richiedere *</label>
              <input required value={item} onChange={(e) => setItem(e.target.value)}
                data-testid="preorder-item-input" className={inputCls}
                placeholder="Es. iPhone 18 colore rosso da 512 GB" />
            </div>
            <div>
              <label className={labelCls}>Note (opzionale)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                data-testid="preorder-notes-input" className={inputCls}
                placeholder="Caparra, urgenza, colore alternativo..." />
            </div>
          </div>

          <div className="flex flex-col justify-end">
            {tags.length > 0 && (
              <div className="mb-6">
                <label className={labelCls}>Categoria *</label>
                <div className="flex flex-wrap gap-2" data-testid="preorder-tag-picker">
                  {tags.map((t) => (
                    <button key={t} type="button" onClick={() => setFormTag(formTag === t ? "" : t)} data-testid={`form-tag-${t}`}
                      className={`px-3.5 py-2 rounded-md text-xs font-semibold border transition-colors duration-150 ${
                        formTag === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button type="submit" disabled={saving} data-testid="preorder-submit-button"
              className="w-full py-3.5 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
              {saving ? "Salvataggio..." : "Aggiungi richiesta"}
            </button>
          </div>
        </div>
      </form>

      <div className="space-y-5">
        <div className="flex gap-2 flex-wrap items-center">
          {[["attesa", "In attesa"], ["consegnato", "Consegnati"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v}-button`}
              className={`px-4 py-2.5 rounded-md text-sm font-semibold border transition-colors duration-150 ${
                filter === v ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"}`}>
              {l}
            </button>
          ))}
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="preorders-sort-select"
            className="px-3.5 py-2.5 rounded-md bg-background border border-border text-sm focus:outline-none focus:border-primary transition-colors duration-150">
            <option value="recenti">Ordina: più recenti</option>
            <option value="coda">Ordina: meno recenti</option>
            <option value="alpha">Ordina: nome A-Z</option>
          </select>
          <div className="relative flex-1 min-w-[220px] max-w-xl">
            <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="preorders-search-input"
              placeholder="Cerca per cliente, telefono o articolo..."
              className="w-full pl-10 pr-3.5 py-2.5 rounded-md bg-background border border-primary/50 text-sm focus:outline-none focus:border-primary transition-colors duration-150" />
          </div>
        </div>

        <div className="flex gap-3 flex-wrap items-center pt-1" data-testid="preorder-tags-bar">
          <button onClick={() => setTagFilter(null)} data-testid="tag-filter-all"
            className={`px-4 py-2 rounded-md text-sm font-bold border transition-colors duration-150 ${
              !tagFilter ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            Tutte
          </button>
          {tags.map((t) => (
            <span key={t} className="relative inline-flex mr-1.5">
              <button onClick={() => setTagFilter(tagFilter === t ? null : t)} data-testid={`tag-button-${t}`}
                className={`px-4 py-2 rounded-md text-sm font-bold border transition-colors duration-150 ${
                  tagFilter === t ? "bg-primary text-primary-foreground border-primary" : "border-primary/40 text-primary hover:bg-primary/5"}`}>
                {t}
              </button>
              <button onClick={() => removeTag(t)} title={`Rimuovi categoria ${t}`} data-testid={`tag-remove-${t}`}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-secondary border border-border text-muted-foreground hover:text-red-500 hover:border-red-500/50 inline-flex items-center justify-center transition-colors duration-150">
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
          {addingTag ? (
            <span className="inline-flex items-center gap-1.5">
              <input value={newTag} onChange={(e) => setNewTag(e.target.value)} autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } if (e.key === "Escape") setAddingTag(false); }}
                placeholder="Es. iPhone usati" data-testid="tag-add-input"
                className="w-44 px-3 py-2 rounded-md bg-background border border-primary text-sm focus:outline-none" />
              <button onClick={addTag} data-testid="tag-add-confirm"
                className="p-2 rounded-md bg-green-600 text-white hover:bg-green-500 transition-colors duration-150">
                <CheckCircle size={16} weight="fill" />
              </button>
            </span>
          ) : (
            <button onClick={() => setAddingTag(true)} data-testid="tag-add-button"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-dashed border-primary/50 text-primary text-sm font-bold hover:bg-primary/5 transition-colors duration-150">
              <Plus size={14} weight="bold" /> Categoria
            </button>
          )}
        </div>
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="preorders-list">
          {preorders.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-12" data-testid="preorders-empty">
              Nessuna richiesta {filter === "attesa" ? "in attesa" : "consegnata"}.
            </p>
          )}
          <div>
            {preorders.map((p, idx) => (
              <div key={p.id} className="px-3.5 py-3.5 sm:px-5 sm:py-4 border-b border-border last:border-b-0 hover:bg-accent/40 transition-colors duration-150"
                data-testid={`preorder-card-${p.id}`}>
                <div className="grid grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 sm:gap-x-4 [grid-template-areas:'a_b'_'a_c'_'d_d'] sm:[grid-template-areas:'a_b_d'_'a_c_d']">
                  <span className={`[grid-area:a] self-center w-12 h-12 sm:w-[72px] sm:h-[72px] rounded-md flex flex-col items-center justify-center gap-0.5 sm:gap-1 px-0.5 sm:px-1 text-center overflow-hidden ${
                    p.status === "consegnato" ? "bg-green-500/15 text-green-500"
                      : positions[p.id] === 1 ? "bg-amber-500/15 text-amber-500"
                      : positions[p.id] <= 3 ? "bg-primary/15 text-primary"
                      : "bg-secondary text-muted-foreground"}`}
                    data-testid={`preorder-status-${p.id}`}>
                    {p.status === "consegnato" ? (
                      <><CheckCircle size={16} weight="fill" />
                      <span className="block text-[7px] sm:text-[9px] font-bold uppercase tracking-tight sm:tracking-wide leading-tight">Consegnato</span></>
                    ) : (
                      <><span className="text-lg sm:text-xl font-extrabold leading-none" data-testid={`preorder-pos-${p.id}`}>#{positions[p.id]}</span>
                      <span className="block text-[7px] sm:text-[9px] font-bold uppercase tracking-tight sm:tracking-wide leading-tight">in coda</span></>
                    )}
                  </span>
                  <div className="[grid-area:b] flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 min-w-0">
                    <span className="font-heading font-bold text-sm sm:text-base truncate">{p.client_name}</span>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-[13px] sm:text-sm font-semibold text-primary truncate" data-testid={`preorder-item-${p.id}`}>{p.item}</span>
                      {p.tag && (
                        <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-[10px] sm:text-[11px] font-bold" data-testid={`preorder-tag-${p.id}`}>{p.tag}</span>
                      )}
                    </span>
                  </div>
                  <div className="[grid-area:c] mt-1.5 sm:mt-2 flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] sm:text-xs text-muted-foreground">
                    {p.client_phone && (
                      <span className="shrink-0 font-bold text-foreground">Tel: <a className="hover:underline" href={`tel:${p.client_phone}`}>{p.client_phone}</a></span>
                    )}
                    {p.notes && <span className="italic truncate">{p.notes}</span>}
                    <span className="text-[10px] text-muted-foreground/70 shrink-0">da {p.created_by}</span>
                  </div>
                  <div className="[grid-area:d] mt-2 pt-2 border-t border-border/60 sm:border-0 sm:pt-0 sm:mt-0 flex items-center justify-between gap-2 sm:flex-col sm:items-end sm:justify-center sm:self-center">
                    <div className="text-left sm:text-right" data-testid={`preorder-date-${p.id}`}>
                      <p className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Prenotato</p>
                      <p className="text-xs sm:text-sm font-extrabold leading-tight">{fmtDate(p.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                    <button onClick={() => openEdit(p)} title="Modifica richiesta" data-testid={`edit-preorder-${p.id}`}
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-150">
                      <PencilSimple size={14} />
                    </button>
                    {p.client_phone && (
                    <button onClick={() => whatsappPreorderReady(p)} title="Avvisa il cliente su WhatsApp"
                      data-testid={`whatsapp-preorder-${p.id}`}
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-md inline-flex items-center justify-center text-green-500 hover:bg-green-500/10 transition-colors duration-150">
                      <WhatsappLogo size={14} weight="fill" />
                    </button>
                  )}
                  {p.status === "attesa" ? (
                    <button onClick={() => setStatus(p.id, "consegnato")} data-testid={`deliver-preorder-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1.5 sm:px-2.5 sm:py-2 rounded-md bg-green-600/90 text-white text-[9px] sm:text-[10px] font-bold uppercase tracking-wider hover:bg-green-500 transition-colors duration-150">
                      <CheckCircle size={13} weight="bold" /> Consegna
                    </button>
                  ) : (
                    <button onClick={() => setStatus(p.id, "attesa")} data-testid={`restore-preorder-${p.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1.5 sm:px-2.5 sm:py-2 rounded-md border border-amber-500/50 text-amber-500 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider hover:bg-amber-500/10 transition-colors duration-150">
                      <ArrowCounterClockwise size={13} weight="bold" /> Riapri
                    </button>
                  )}
                    {user?.role === "admin" && (
                    <button onClick={() => remove(p.id)} title="Elimina prenotazione" data-testid={`delete-preorder-${p.id}`}
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-accent transition-colors duration-150">
                      <Trash size={14} />
                    </button>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="preorder-edit-dialog">
          <form onSubmit={saveEdit} className="w-full max-w-md border border-border rounded-md bg-card p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold tracking-tight">Modifica richiesta</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="preorder-edit-close"
                className="w-8 h-8 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="preorder-edit-client">
              Cliente: <span className="font-bold text-foreground">{editing.client_name}</span> — la posizione in coda resta invariata.
            </p>
            <div>
              <label className={labelCls}>Cosa vuole richiedere *</label>
              <input required value={editItem} onChange={(e) => setEditItem(e.target.value)}
                data-testid="preorder-edit-item-input" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Note (opzionale)</label>
              <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3}
                data-testid="preorder-edit-notes-input" className={inputCls} />
            </div>
            {tags.length > 0 && (
              <div>
                <label className={labelCls}>Categoria *</label>
                <div className="flex flex-wrap gap-2" data-testid="preorder-edit-tag-picker">
                  {tags.map((t) => (
                    <button key={t} type="button" onClick={() => setEditTag(editTag === t ? "" : t)} data-testid={`edit-tag-${t}`}
                      className={`px-3.5 py-2 rounded-md text-xs font-semibold border transition-colors duration-150 ${
                        editTag === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setEditing(null)} data-testid="preorder-edit-cancel"
                className="flex-1 py-3 rounded-md border border-border text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150">
                Annulla
              </button>
              <button type="submit" disabled={editSaving} data-testid="preorder-edit-save"
                className="flex-1 py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
                {editSaving ? "Salvataggio..." : "Salva modifiche"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
